import { Link } from "react-router-dom";
import { formatMoney } from "@bw/shared/utils";
import type { BookingResponse } from "@bw/shared/types/generated";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * One row of the player's bookings list: the court name (rendered ONCE per court
 * group, on the group's first row, so it is not repeated), the start time, the
 * scoped amount, a "View" link into the per-booking detail hub, and a host-only
 * Cancel action (when the booking is still cancellable). An accepted participant
 * gets a truthful "Going" state and an em dash for redacted money.
 *
 * All values come straight from the BookingResponse — the start time, the
 * server's amount_charged, and the cancelled state. Nothing is fabricated. A
 * cancelled booking shows its server-set refund_amount and is no longer
 * cancellable.
 *
 * Cancellability is SERVER state, not a client guess. The backend rejects a
 * cancel on a terminal booking (completed / no-show / a past start_time), so the
 * row must NOT offer a Cancel button the server will refuse — that would be a
 * client-invented affordance. Until BookingResponse exposes an explicit
 * `is_cancellable` flag (tracked as a backend follow-up), we gate conservatively
 * on the only honest signal we have: status === "confirmed" AND a future
 * start_time. Anything else renders its terminal state instead.
 *
 * The "View" link always routes to the per-booking detail hub
 * (/book/:facilityId/bookings/:bookingId) — the roster + check-in QR + (for the
 * host) add/remove — so a booking row is never a dead end.
 */

/** Venue-local "Tue, Jun 20 · 5:00 PM" for a booking start, best-effort. */
function whenLabel(startTime: string, endTime?: string): string {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : null;
    if (end && !Number.isNaN(end.getTime())) return formatter.formatRange(start, end);
    return formatter.format(start);
  } catch {
    return startTime;
  }
}

/**
 * The compact date badge parts (prominent day number + short accent month) for a
 * booking start. Purely decorative — the row's title still carries the full
 * date/time, so a screen reader hears the complete date. Returns null on an
 * unparseable start_time so the row degrades to no badge. */
function dateBadge(startTime: string): { day: string; month: string } | null {
  const d = new Date(startTime);
  if (Number.isNaN(d.getTime())) return null;
  return {
    day: new Intl.DateTimeFormat("en-US", { day: "numeric" }).format(d),
    month: new Intl.DateTimeFormat("en-US", { month: "short" })
      .format(d)
      .toUpperCase(),
  };
}

export function BookingHistoryRow({
  booking,
  courtName,
  isHost,
  onCancel,
}: {
  booking: BookingResponse;
  /** The court name to show on THIS row — set only on a court group's first row
   *  so the court label is not repeated down the group. */
  courtName?: string;
  /** Whether the authenticated viewer owns this booking. The backend remains
   *  authoritative; this only keeps host-only cancellation out of guest UI. */
  isHost: boolean;
  onCancel: (booking: BookingResponse) => void;
}) {
  const isCancelled = booking.status === "cancelled";
  const isCancellationPending = booking.cancellation_recovery_pending === true;
  const currency = booking.currency ?? "USD";

  // Cancellable only when the server-set status is "confirmed" AND the booking
  // has not started yet. A terminal/past booking (completed, no-show, or a past
  // start_time) is rejected server-side, so we never render a Cancel button the
  // backend will refuse. `Date.parse` returns NaN for an unparseable start_time;
  // treat that as not-future (no Cancel) rather than guessing.
  const startMs = Date.parse(booking.start_time);
  const isFuture = !Number.isNaN(startMs) && startMs > Date.now();
  const isCancellable =
    isHost &&
    booking.status === "confirmed" &&
    !isCancellationPending &&
    isFuture;

  const detailHref = `/book/${booking.facility_id}/bookings/${booking.booking_id}`;

  // Date tile — decorative (aria-hidden); the authoritative date is
  // the row title (whenLabel) below, so the SR experience is unchanged.
  const badge = dateBadge(booking.start_time);

  return (
    <ListItem
      data-cancelled={isCancelled || undefined}
      divider
      sx={{ px: 2, py: 1.5, gap: 1.5, alignItems: "center" }}
    >
      {badge ? (
        <Box aria-hidden="true" sx={{ minWidth: 44, textAlign: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>
            {badge.day}
          </Typography>
          <Typography
            variant="caption"
            color="primary.main"
            sx={{ fontWeight: 800 }}
          >
            {badge.month}
          </Typography>
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
          {booking.facility_name?.trim() || "Facility"}
        </Typography>
        {courtName ? (
          <Typography sx={{ fontWeight: 800 }}>{courtName}</Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          {whenLabel(booking.start_time, booking.end_time)}
        </Typography>
        {booking.player_count > 1 ? (
          <Typography variant="caption" color="text.secondary">
            {booking.player_count} players
          </Typography>
        ) : null}
      </Box>
      <Stack spacing={0.75} sx={{ alignItems: "flex-end" }}>
        <Typography
          sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
        >
          {formatMoney(booking.amount_charged, currency)}
        </Typography>
        <Button component={Link} size="small" to={detailHref}>
          View
        </Button>
        {isCancelled ? (
          <Chip
            size="small"
            color="error"
            variant="outlined"
            label={
              booking.refund_amount != null
                ? `Cancelled · ${formatMoney(booking.refund_amount, currency)} refunded`
                : "Cancelled"
            }
          />
        ) : isCancellationPending ? (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label="Cancellation processing"
          />
        ) : isCancellable ? (
          <Button
            type="button"
            size="small"
            color="error"
            onClick={() => onCancel(booking)}
          >
            Cancel
          </Button>
        ) : (
          <Chip
            size="small"
            variant="outlined"
            label={
              booking.status === "confirmed" && isFuture
                ? "Going"
                : terminalLabel(booking.status)
            }
          />
        )}
      </Stack>
    </ListItem>
  );
}

/** Human-readable terminal/non-cancellable state for a booking row. */
function terminalLabel(status: string): string {
  switch (status) {
    case "completed":
      return "Completed";
    case "no_show":
    case "no-show":
      return "No-show";
    default:
      return "Past";
  }
}
